import { useEffect, useState } from 'react';
import { ApiError, api } from '../api/client';
import type { AdminUser, Device } from '../api/types';
import {
  IconDownload,
  IconCar,
  IconEye,
  IconEyeOff,
  IconKey,
  IconPencil,
  IconPlus,
  IconTrash,
  IconUsers,
} from '../components/Icons';
import { ClaimRequests } from './ClaimRequests';
import { MODELS } from '../models';
import { useToast } from '../components/Toast';
import { useT } from '../i18n';

interface Props {
  devices: Device[];
  onDevicesChanged: () => void;
}

/**
 * Readable but strong: no characters that get misread when the admin reads the
 * password down the phone to a customer.
 */
function generatePassword(): string {
  const alphabet = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint32Array(14));
  return Array.from(bytes, (n) => alphabet[n % alphabet.length]).join('');
}

export function Admin({ devices, onDevicesChanged }: Props) {
  const { t } = useT();
  const [tab, setTab] = useState<'requests' | 'devices' | 'users'>('requests');
  const [pending, setPending] = useState(0);

  // The badge is the only thing that tells you a customer is waiting, so it
  // refreshes on every visit to the tab bar rather than only on first mount.
  const refreshPending = () => {
    api.admin
      .pendingClaims()
      .then(setPending)
      .catch(() => setPending(0));
  };

  useEffect(refreshPending, [devices]);

  return (
    <div className="admin-layout page">
      <div className="tab-row" style={{ width: 'fit-content' }}>
        <button
          type="button"
          className={tab === 'requests' ? 'tab active' : 'tab'}
          onClick={() => setTab('requests')}
        >
          {t('requests')}
          {pending > 0 && <span className="tab-badge">{pending}</span>}
        </button>
        <button
          type="button"
          className={tab === 'devices' ? 'tab active' : 'tab'}
          onClick={() => setTab('devices')}
        >
          {t('devices')}
        </button>
        <button
          type="button"
          className={tab === 'users' ? 'tab active' : 'tab'}
          onClick={() => setTab('users')}
        >
          {t('users')}
        </button>
      </div>

      {tab === 'requests' && (
        <ClaimRequests
          onChanged={() => {
            refreshPending();
            onDevicesChanged();
          }}
        />
      )}
      {tab === 'devices' && <DeviceAdmin devices={devices} onChanged={onDevicesChanged} />}
      {tab === 'users' && <UserAdmin devices={devices} />}

      <BackupCard />
    </div>
  );
}

/**
 * The operator's own copy of everything.
 *
 * A plain link, not a fetch: the file is streamed and can run to hundreds of
 * megabytes, and pulling that through JavaScript would hold it all in the tab's
 * memory before writing a byte to disk. The browser's own downloader handles it
 * without ever assembling the whole thing.
 */
function BackupCard() {
  const { t } = useT();
  const toast = useToast();

  return (
    <div className="admin-section" style={{ marginTop: 24 }}>
      <span className="section-label">{t('backup')}</span>
      <p className="muted small" style={{ maxWidth: '58ch', marginTop: 4 }}>
        {t('backupHint')}
      </p>
      <a
        className="btn"
        href={api.backupUrl()}
        download
        onClick={() => toast('info', t('backupStarted'))}
      >
        <IconDownload />
        {t('backup')}
      </a>
    </div>
  );
}

function DeviceAdmin({ devices, onChanged }: { devices: Device[]; onChanged: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);

  // Only to turn claimed_by ids into names in the owner column.
  useEffect(() => {
    api.admin.users().then(setUsers).catch(() => setUsers([]));
  }, [devices]);

  const ownerName = (id: number | null | undefined) =>
    users.find((u) => u.id === id)?.name ?? `#${id}`;
  const [form, setForm] = useState({
    imei: '',
    name: '',
    model: 'GT06N',
    plate: '',
    simNumber: '',
    hasImmobiliser: true,
  });
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState<Device | null>(null);
  const [edit, setEdit] = useState({
    name: '',
    model: MODELS[0] as string,
    plate: '',
    simNumber: '',
    hasImmobiliser: true,
    disabled: false,
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (device: Device) => {
    setEditing(device);
    setEdit({
      name: device.name,
      model: device.model,
      plate: device.plate ?? '',
      simNumber: device.simNumber ?? '',
      hasImmobiliser: device.hasImmobiliser,
      disabled: device.disabled,
    });
  };

  const saveEdit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    setSavingEdit(true);
    try {
      await api.admin.updateDevice(editing.id, {
        name: edit.name.trim(),
        model: edit.model,
        plate: edit.plate.trim() || null,
        simNumber: edit.simNumber.trim() || null,
        hasImmobiliser: edit.hasImmobiliser,
        disabled: edit.disabled,
      });
      toast('ok', t('deviceUpdated'));
      setEditing(null);
      onChanged();
    } catch (cause) {
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setSavingEdit(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.admin.createDevice({
        imei: form.imei.trim(),
        name: form.name.trim(),
        model: form.model,
        plate: form.plate.trim() || undefined,
        simNumber: form.simNumber.trim() || undefined,
        hasImmobiliser: form.hasImmobiliser,
      });
      setForm({
        imei: '',
        name: '',
        model: 'GT06N',
        plate: '',
        simNumber: '',
        hasImmobiliser: true,
      });
      toast('ok', t('deviceCreated'));
      onChanged();
    } catch (cause) {
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setBusy(false);
    }
  };

  const toggleCapture = async (device: Device) => {
    const active =
      device.captureUntil !== null &&
      device.captureUntil !== undefined &&
      Date.parse(device.captureUntil) > Date.now();
    try {
      await api.admin.captureFrames(device.id, active ? 0 : 10);
      toast('ok', active ? t('captureStopped') : t('captureStarted'));
      onChanged();
    } catch (cause) {
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    }
  };

  const release = async (device: Device) => {
    if (!confirm(`${t('confirmRelease')}\n${device.name}`)) return;
    try {
      await api.admin.releaseDevice(device.id);
      toast('ok', t('deviceReleased'));
      onChanged();
    } catch (cause) {
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    }
  };

  const remove = async (device: Device) => {
    // Deleting a device drops all of its history, so make the admin say yes.
    if (!confirm(`${t('confirmDelete')}\n${device.name} (${device.imei})`)) return;
    try {
      await api.admin.deleteDevice(device.id);
      toast('ok', t('deviceDeleted'));
      onChanged();
    } catch (cause) {
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    }
  };

  return (
    <div className="admin-grid">
      <form className="card" onSubmit={submit}>
        <div className="card-head">
          <IconCar />
          <h3>{t('addDevice')}</h3>
        </div>

        <label>
          {t('imei')}
          <input
            value={form.imei}
            onChange={(e) => setForm({ ...form, imei: e.target.value })}
            required
            dir="ltr"
            inputMode="numeric"
            placeholder="860000000000001"
          />
        </label>

        <label>
          {t('name')}
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </label>

        <label>
          {t('model')}
          <select value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}>
            {MODELS.map((model) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
        </label>

        <label>
          {t('plate')}
          <input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} />
        </label>

        <label>
          {t('simNumber')}
          <input
            value={form.simNumber}
            onChange={(e) => setForm({ ...form, simNumber: e.target.value })}
            dir="ltr"
          />
        </label>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={form.hasImmobiliser}
            onChange={(e) => setForm({ ...form, hasImmobiliser: e.target.checked })}
          />
          {t('hasImmobiliser')}
        </label>

        <button type="submit" className="btn primary block" disabled={busy}>
          {busy ? <span className="btn-spinner" /> : <IconPlus />}
          {t('save')}
        </button>

      </form>

      <div className="card">
        <div className="card-head">
          <h3>{t('devices')}</h3>
          <span className="count">{devices.length}</span>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('imei')}</th>
                <th>{t('model')}</th>
                <th>{t('owner')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr key={device.id}>
                  <td>
                    <strong>{device.name}</strong>
                    {device.plate && (
                      <>
                        <br />
                        <small className="muted">{device.plate}</small>
                      </>
                    )}
                  </td>
                  <td dir="ltr">{device.imei}</td>
                  <td>
                    {device.model} <span className="tag brand">{device.protocol}</span>
                    <br />
                    <span className={device.online ? 'pill online' : 'pill offline'}>
                      {device.online ? t('online') : t('offline')}
                    </span>
                  </td>
                  <td>
                    {device.claimedAt ? (
                      <>
                        <span className="tag brand">{ownerName(device.claimedBy)}</span>
                        <br />
                        <button
                          type="button"
                          className="link-btn tiny-link danger"
                          onClick={() => release(device)}
                        >
                          {t('releaseDevice')}
                        </button>
                      </>
                    ) : (
                      <span className="tag">{t('unclaimed')}</span>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button type="button" className="link-btn" onClick={() => openEdit(device)}>
                        <IconPencil size={13} /> {t('edit')}
                      </button>
                      <button
                        type="button"
                        className="link-btn"
                        title={t('captureHint')}
                        onClick={() => toggleCapture(device)}
                      >
                        {device.captureUntil && Date.parse(device.captureUntil) > Date.now()
                          ? t('capturing')
                          : t('capture')}
                      </button>
                      <button
                        type="button"
                        className="link-btn danger"
                        onClick={() => remove(device)}
                        aria-label={t('delete')}
                      >
                        <IconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <form className="modal scrollable" onSubmit={saveEdit}>
            <h3>
              {t('editDevice')} — {editing.name}
            </h3>
            <p className="muted tiny" dir="ltr" style={{ margin: 0 }}>
              IMEI {editing.imei}
            </p>
            <p className="muted tiny" style={{ margin: 0 }}>
              {t('imeiCannotChange')}
            </p>

            <label>
              {t('name')}
              <input
                value={edit.name}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                required
              />
            </label>

            <label>
              {t('model')}
              <select
                value={edit.model}
                onChange={(e) => setEdit({ ...edit, model: e.target.value })}
              >
                {MODELS.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </label>

            <label>
              {t('plate')}
              <input
                value={edit.plate}
                onChange={(e) => setEdit({ ...edit, plate: e.target.value })}
              />
            </label>

            <label>
              {t('simNumber')}
              <input
                dir="ltr"
                value={edit.simNumber}
                onChange={(e) => setEdit({ ...edit, simNumber: e.target.value })}
              />
            </label>

            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={edit.hasImmobiliser}
                onChange={(e) => setEdit({ ...edit, hasImmobiliser: e.target.checked })}
              />
              {t('hasImmobiliser')}
            </label>

            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={!edit.disabled}
                onChange={(e) => setEdit({ ...edit, disabled: !e.target.checked })}
              />
              {t('deviceActive')}
            </label>

            <div className="btn-grid">
              <button type="submit" className="btn primary" disabled={savingEdit}>
                {savingEdit && <span className="btn-spinner" />}
                {t('save')}
              </button>
              <button type="button" className="btn" onClick={() => setEditing(null)}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function UserAdmin({ devices }: { devices: Device[] }) {
  const { t } = useT();
  const toast = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [form, setForm] = useState({ email: '', name: '', password: '', phone: '' });
  const [busy, setBusy] = useState(false);
  // Kept only in this component's memory, and only until dismissed: the server
  // stores a hash, so this is the one moment the value can be handed over.
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [granted, setGranted] = useState<Set<number>>(new Set());
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);

  const [details, setDetails] = useState<AdminUser | null>(null);
  const [detailsForm, setDetailsForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'user' as 'admin' | 'user',
    disabled: false,
    password: '',
  });
  const [savingDetails, setSavingDetails] = useState(false);
  const [revealNew, setRevealNew] = useState(false);
  const [resetIssued, setResetIssued] = useState<{ email: string; password: string } | null>(null);

  const reload = async () => {
    try {
      setUsers(await api.admin.users());
    } catch (cause) {
      toast('err', (cause as Error).message);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.admin.createUser({
        email: form.email.trim(),
        name: form.name.trim(),
        password: form.password,
        phone: form.phone.trim() || undefined,
      });
      setIssued({ email: form.email.trim(), password: form.password });
      setForm({ email: '', name: '', password: '', phone: '' });
      toast('ok', t('userCreated'));
      await reload();
    } catch (cause) {
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setBusy(false);
    }
  };

  const openDetails = (user: AdminUser) => {
    setDetails(user);
    setRevealNew(false);
    setDetailsForm({
      name: user.name,
      email: user.email,
      phone: user.phone ?? '',
      role: user.role,
      disabled: user.disabled,
      password: '',
    });
  };

  const saveDetails = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!details) return;
    setSavingDetails(true);
    try {
      const result = await api.admin.updateUser(details.id, {
        name: detailsForm.name.trim(),
        email: detailsForm.email.trim(),
        phone: detailsForm.phone.trim() || null,
        role: detailsForm.role,
        disabled: detailsForm.disabled,
        // Only sent when actually filled in, so saving other fields does not
        // silently reset the customer's password.
        ...(detailsForm.password ? { password: detailsForm.password } : {}),
      });
      toast('ok', result.passwordChanged ? t('passwordReset') : t('userUpdated'));
      if (result.passwordChanged) {
        setResetIssued({ email: detailsForm.email.trim(), password: detailsForm.password });
      }
      setDetails(null);
      await reload();
    } catch (cause) {
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setSavingDetails(false);
    }
  };

  const removeUser = async (user: AdminUser) => {
    if (!confirm(`${t('confirmDeleteUser')}\n\n${user.name} — ${user.email}`)) return;
    try {
      await api.admin.deleteUser(user.id);
      toast('ok', t('userDeleted'));
      await reload();
    } catch (cause) {
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    }
  };

  /**
   * Open the access editor pre-filled with what the user already has.
   * The save below replaces the entire grant set, so starting from a blank list
   * would silently revoke every vehicle the admin did not re-tick.
   */
  const openAccess = async (user: AdminUser) => {
    setEditing(user);
    setGranted(new Set());
    setLoadingAccess(true);
    try {
      const current = await api.admin.userDevices(user.id);
      setGranted(new Set(current.map((grant) => grant.deviceId)));
    } catch (cause) {
      toast('err', (cause as Error).message);
      setEditing(null);
    } finally {
      setLoadingAccess(false);
    }
  };

  const saveAccess = async () => {
    if (!editing) return;
    setSavingAccess(true);
    try {
      await api.admin.setUserDevices(
        editing.id,
        [...granted].map((deviceId) => ({ deviceId, level: 'owner' as const })),
      );
      toast('ok', t('accessSaved'));
      setEditing(null);
      await reload();
    } catch (cause) {
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setSavingAccess(false);
    }
  };

  return (
    <div className="admin-grid">
      <form className="card" onSubmit={submit}>
        <div className="card-head">
          <IconUsers />
          <h3>{t('addUser')}</h3>
        </div>

        <label>
          {t('name')}
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </label>

        <label>
          {t('email')}
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            dir="ltr"
          />
        </label>

        <label>
          {t('password')}
          <span className="input-with-action">
            <input
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={8}
              dir="ltr"
            />
            <button
              type="button"
              className="reveal-btn wide"
              onClick={() => setForm({ ...form, password: generatePassword() })}
            >
              {t('generate')}
            </button>
          </span>
        </label>

        <label>
          {t('phone')}
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            dir="ltr"
          />
        </label>

        <button type="submit" className="btn primary block" disabled={busy}>
          {busy ? <span className="btn-spinner" /> : <IconPlus />}
          {t('save')}
        </button>

        {issued && <CredentialCard issued={issued} onClose={() => setIssued(null)} />}
      </form>

      <div className="card">
        <div className="card-head">
          <h3>{t('users')}</h3>
          <span className="count">{users.length}</span>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('email')}</th>
                <th>{t('assignedVehicles')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.name}</strong>
                    <br />
                    <span className={user.role === 'admin' ? 'tag brand' : 'tag'}>{user.role}</span>
                    {user.self_registered && (
                      <span className="tag" style={{ marginInlineStart: 4 }}>
                        {t('selfRegistered')}
                      </span>
                    )}
                    {user.disabled && (
                      <span className="tag danger-tag" style={{ marginInlineStart: 4 }}>
                        {t('accountDisabled')}
                      </span>
                    )}
                  </td>
                  <td dir="ltr">{user.email}</td>
                  <td>
                    <span className="count">{user.device_count}</span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button type="button" className="link-btn" onClick={() => openDetails(user)}>
                        <IconPencil size={13} /> {t('edit')}
                      </button>
                      <button type="button" className="link-btn" onClick={() => openAccess(user)}>
                        {t('manageAccess')}
                      </button>
                      <button
                        type="button"
                        className="link-btn danger"
                        onClick={() => removeUser(user)}
                        aria-label={t('delete')}
                      >
                        <IconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {resetIssued && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <CredentialCard issued={resetIssued} onClose={() => setResetIssued(null)} />
          </div>
        </div>
      )}

      {details && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <form className="modal" onSubmit={saveDetails}>
            <h3>
              {t('editUser')} — {details.name}
            </h3>

            <label>
              {t('name')}
              <input
                value={detailsForm.name}
                onChange={(e) => setDetailsForm({ ...detailsForm, name: e.target.value })}
                required
                minLength={2}
              />
            </label>

            <label>
              {t('email')}
              <input
                type="email"
                value={detailsForm.email}
                onChange={(e) => setDetailsForm({ ...detailsForm, email: e.target.value })}
                required
                dir="ltr"
              />
            </label>

            <label>
              {t('phone')}
              <input
                value={detailsForm.phone}
                onChange={(e) => setDetailsForm({ ...detailsForm, phone: e.target.value })}
                dir="ltr"
                inputMode="tel"
              />
            </label>

            <label>
              {t('role')}
              <select
                value={detailsForm.role}
                onChange={(e) =>
                  setDetailsForm({ ...detailsForm, role: e.target.value as 'admin' | 'user' })
                }
              >
                <option value="user">{t('roleUser')}</option>
                <option value="admin">{t('roleAdmin')}</option>
              </select>
            </label>

            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={!detailsForm.disabled}
                onChange={(e) => setDetailsForm({ ...detailsForm, disabled: !e.target.checked })}
              />
              {t('accountActive')}
            </label>

            <label>
              {t('newPassword')}
              <span className="input-with-action">
                <input
                  type={revealNew ? 'text' : 'password'}
                  value={detailsForm.password}
                  onChange={(e) => setDetailsForm({ ...detailsForm, password: e.target.value })}
                  minLength={8}
                  dir="ltr"
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="reveal-btn"
                  onClick={() => setRevealNew((v) => !v)}
                  aria-label={revealNew ? t('hidePassword') : t('showPassword')}
                >
                  {revealNew ? <IconEyeOff /> : <IconEye />}
                </button>
              </span>
            </label>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span className="muted tiny">{t('newPasswordHint')}</span>
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setDetailsForm({ ...detailsForm, password: generatePassword() });
                  setRevealNew(true);
                }}
              >
                {t('generate')}
              </button>
            </div>

            <div className="btn-grid">
              <button type="submit" className="btn primary" disabled={savingDetails}>
                {savingDetails && <span className="btn-spinner" />}
                {t('save')}
              </button>
              <button type="button" className="btn" onClick={() => setDetails(null)}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <h3>
              {t('manageAccess')} — {editing.name}
            </h3>

            {loadingAccess ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="skeleton" style={{ height: 36 }} />
                ))}
              </div>
            ) : devices.length === 0 ? (
              <p className="muted small">{t('noDevicesYet')}</p>
            ) : (
              <>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}
                >
                  <span className="muted small">
                    {t('currentlyAssigned')}: <strong>{granted.size}</strong> / {devices.length}
                  </span>
                  <span style={{ display: 'flex', gap: 12 }}>
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => setGranted(new Set(devices.map((d) => d.id)))}
                    >
                      {t('selectAll')}
                    </button>
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => setGranted(new Set())}
                    >
                      {t('clearAll')}
                    </button>
                  </span>
                </div>

                <div className="checkbox-list">
                  {devices.map((device) => (
                    <label key={device.id} className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={granted.has(device.id)}
                        onChange={(event) => {
                          const next = new Set(granted);
                          if (event.target.checked) next.add(device.id);
                          else next.delete(device.id);
                          setGranted(next);
                        }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {device.name}{' '}
                        <small className="muted">{device.plate ?? device.imei}</small>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )}

            <div className="btn-grid">
              <button
                type="button"
                className="btn primary"
                onClick={saveAccess}
                disabled={loadingAccess || savingAccess}
              >
                {savingAccess && <span className="btn-spinner" />}
                {t('save')}
              </button>
              <button type="button" className="btn" onClick={() => setEditing(null)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Shows a freshly set password once, with a copy button.
 *
 * The server only ever stores a bcrypt hash, so this is the single moment the
 * value exists in readable form. Storing it in plaintext so it could be looked
 * up later would mean one database leak exposes every customer's password —
 * and people reuse passwords.
 */
function CredentialCard({
  issued,
  onClose,
}: {
  issued: { email: string; password: string };
  onClose: () => void;
}) {
  const { t } = useT();
  const toast = useToast();

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast('ok', t('copied'));
    } catch {
      // Clipboard needs a secure context; the value is on screen regardless.
      toast('err', t('error'));
    }
  };

  return (
    <div className="issued-code">
      <span className="code-head">
        <IconKey size={13} />
        {t('credentialsReady')}
      </span>

      <button type="button" className="cred-line" onClick={() => copy(issued.email)}>
        <span className="cred-label">{t('email')}</span>
        <code dir="ltr">{issued.email}</code>
      </button>

      <button type="button" className="cred-line" onClick={() => copy(issued.password)}>
        <span className="cred-label">{t('password')}</span>
        <code dir="ltr">{issued.password}</code>
      </button>

      <p className="muted tiny" style={{ margin: 0 }}>
        {t('passwordShownOnce')}
      </p>

      <div className="btn-row">
        <button
          type="button"
          className="btn"
          onClick={() => copy(`${issued.email}\n${issued.password}`)}
        >
          {t('copyAll')}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          {t('close')}
        </button>
      </div>
    </div>
  );
}
